import { Component } from '@theme/component';
import { debounce, getDocumentScrollY } from '@theme/utilities';
import { MegaMenuHoverEvent, ThemeEvents } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/*
 * Duration used for the close-animation fallback timer
 * (the normal path relies on `transitionend`, not this timer).
 *
 * clip (THEME_SEARCH_MENU_DEFAULT_CLIP_MS = 280):
 *   Must match `--submenu-animation-speed` on `search-menu`
 *   in `snippets/search.liquid`.
 *   The dropdown uses `clip-path` for the closing animation,
 *   and `transitionend` is based on this duration. If you update
 *   the CSS, keep this in sync with those values.
 *
 * buffer (THEME_SEARCH_MENU_DEFAULT_CLOSE_FALLBACK_BUFFER_MS = 150):
 *   Only used when the browser fails to fire `transitionend`,
 *   in which case `setTimeout` is used as a fallback.
 *   If `finalize` runs before the animation completes, you may see
 *   a visual snap. Therefore, the fallback delay = clip + buffer,
 *   slightly longer than the clip duration.
 *
 * Optional: set `globalThis.themeSearchMenuSubmenuClipMs` or
 * `globalThis.themeSearchMenuCloseFallbackBufferMs` *before* this module loads to override.
 */
const THEME_SEARCH_MENU_DEFAULT_CLIP_MS = 280;
const THEME_SEARCH_MENU_DEFAULT_CLOSE_FALLBACK_BUFFER_MS = 150;

/** @type {typeof globalThis & { themeSearchMenuSubmenuClipMs?: number; themeSearchMenuCloseFallbackBufferMs?: number }} */
const root = globalThis;

function getSearchMenuCloseFallbackDelayMs() {
  const clipMs =
    typeof root.themeSearchMenuSubmenuClipMs === 'number'
      ? root.themeSearchMenuSubmenuClipMs
      : THEME_SEARCH_MENU_DEFAULT_CLIP_MS;
  const bufferMs =
    typeof root.themeSearchMenuCloseFallbackBufferMs === 'number'
      ? root.themeSearchMenuCloseFallbackBufferMs
      : THEME_SEARCH_MENU_DEFAULT_CLOSE_FALLBACK_BUFFER_MS;
  return clipMs + bufferMs;
}

function isSwymWishlistModalOpen() {
  const el = document.querySelector('.swym-modal.swym-add-wishlist-modal, .swym-add-wishlist-modal');
  if (!(el instanceof HTMLElement)) return false;
  if (el.offsetParent !== null) return true;
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

/**
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class SearchMenu extends Component {
  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  #abortController = new AbortController();
  /**
   * @type {AbortController | null}
   */
  #submenuAbortController = null;
  /**
   * @type {MutationObserver | null}
   */
  #submenuObserver = null;
  /**
   * @type {ResizeObserver | null}
   */
  #submenuResizeObserver = null;
  /**
   * @type {number | null}
   */
  #scrollY = null;
  /**
   * @type {boolean}
   */
  #isClosing = false;
  /**
   * @type {HTMLElement | null}
   */
  #cachedSubmenu = null;
  /**
   * @type {number | null}
   */
  #lastPointerEnterTime = null;
  /**
   * Latest `updateHeight` for the open search dropdown; predictive search calls `theme:submenu-layout`
   * after morph so clip + `--search-dropdown-results-max-height` run when the DOM is up to date.
   *
   * @type {(() => void) | null}
   */
  #submenuLayoutCallback = null;
  /**
   * @type {AbortController | null}
   */
  #transitionAbortController = null;

  /** @type {number | null} */
  #dropdownOpenInnerWidth = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    document.addEventListener(ThemeEvents.megaMenuHover, this.#handleMegaMenuHover, { signal });
    document.addEventListener('keydown', this.#handleKeyDown, { signal });
    window.addEventListener('resize', this.#handleResize, { signal });
    // Fired from `predictive-search.js` after results markup is morphed (footer / `:has()` may lag one frame).
    this.addEventListener('theme:submenu-layout', this.#handlePredictiveSearchLayout, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#submenuObserver?.disconnect();
    this.#submenuResizeObserver?.disconnect();
    this.#transitionAbortController?.abort();
    this.#unlockBodyScroll();
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getActiveSubmenu() {
    if (!this.#state.activeItem) return null;
    return findSubmenu(this.#state.activeItem);
  }

  /**
   * Minimum block size for the results scroller (matches theme touch target when available).
   *
   * @returns {number}
   */
  #resultsScrollerMinHeightPx() {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--minimum-touch-target').trim();
    const parsed = Number.parseFloat(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 44;
  }

  /**
   * Pixels from the top edge of `submenu` (outer .menu-list__submenu) to the layout viewport bottom.
   * Using innerHeight minus global header height is wrong here: the submenu already sits under the
   * header row, so subtracting full header again (or missing row offset) leaves a few px gap at the
   * bottom of the screen.
   *
   * @param {HTMLElement} submenu
   * @returns {number}
   */
  #maxSubmenuClipHeightPx(submenu) {
    const top = submenu.getBoundingClientRect().top;
    return Math.max(0, Math.ceil(window.innerHeight - top));
  }

  /**
   * Footer visibility in the header dropdown follows CSS in `search-modal.liquid`
   * (`:has([data-search-results])` and no `.predictive-search-results__no-results`). JS uses the same
   * rules so we reserve vertical space for the footer when computing the results scroller height.
   *
   * @param {HTMLElement} submenu
   */
  #footerReserveSpaceForSubmenu(submenu) {
    const inner = submenu.querySelector('.predictive-search-results__inner');
    if (!(inner instanceof HTMLElement) || !inner.hasAttribute('data-search-results')) return false;
    if (submenu.querySelector('.predictive-search-results__no-results')) return false;
    return true;
  }

  /** Re-measure after predictive search updates; double `rAF` lets `:has()` / footer layout settle. */
  #handlePredictiveSearchLayout = () => {
    requestAnimationFrame(() => {
      this.#submenuLayoutCallback?.();
      requestAnimationFrame(() => {
        this.#submenuLayoutCallback?.();
      });
    });
  };

  /**
   * @param {Element | null} element
   * @returns {boolean}
   */
  #isWithinSearchMenu(element) {
    if (!element) return false;
    return this.contains(element) || this.#cachedSubmenu?.contains(element) || false;
  }

  /**
   * @param {EventTarget | null} relatedTarget
   */
  #pointerLeftSiteHeader(relatedTarget) {
    const header = document.querySelector('header-component');
    if (!(relatedTarget instanceof Node)) return true;
    if (!header) return true;
    return !header.contains(relatedTarget);
  }

  /**
   * @param {PointerEvent} event
   */
  #handleSubmenuPointerLeave = (event) => {
    if (event.pointerType !== 'mouse') return;
    if (!this.#pointerLeftSiteHeader(event.relatedTarget)) return;
    this.#debouncedDeactivate();
  };

  /**
   * @param {PointerEvent} event
   */
  #handleHeaderPointerLeave = (event) => {
    if (!this.#state.activeItem) return;
    if (event.pointerType !== 'mouse') return;
    if (!this.#pointerLeftSiteHeader(event.relatedTarget)) return;
    this.#debouncedDeactivate();
  };

  get submenuHovered() {
    const submenu = this.#getActiveSubmenu();
    return submenu?.matches(':hover') ?? false;
  }

  /**
   * @param {Event} event
   */
  #handleMegaMenuHover = (event) => {
    if (event.target !== this && this.#state.activeItem) {
      this.#deactivate();
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.#state.activeItem) return;

    const activeElement = document.activeElement;
    const isWithinSearchMenu = this.#isWithinSearchMenu(activeElement);

    if (isWithinSearchMenu || !activeElement || activeElement === document.body) {
      event.preventDefault();
      event.stopPropagation();
      this.#isClosing = true;
      this.#deactivate(undefined, true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const button = this.querySelector('[ref="menuitem"]');
          if (button instanceof HTMLElement) {
            button.focus();
          }
          this.#isClosing = false;
        });
      });
    }
  };

  /**
   * Close the dropdown when the window width changes while open.
   */
  #handleResize = debounce(() => {
    if (!this.#state.activeItem || this.#dropdownOpenInnerWidth === null) return;
    if (window.innerWidth === this.#dropdownOpenInnerWidth) return;
    this.#dropdownOpenInnerWidth = null;
    this.#deactivate(undefined, true);
  }, 50);

  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? Number.parseInt(value, 10) : 0;
  }

  /**
   * @param {MouseEvent} event
   */
  toggle = (event) => {
    event.preventDefault();
    event.stopPropagation();

    this.#lastPointerEnterTime = null;

    if (this.#state.activeItem) {
      this.#deactivate(undefined, true);
    } else {
      this.#debouncedDeactivate.cancel();
      this.#debouncedActivateHandler.cancel();
      this.#debouncedActivateHandler(event);
    }
  };

  /**
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (this.#isClosing) return;

    if (event instanceof PointerEvent) {
      if (event.pointerType !== 'mouse') {
        this.#lastPointerEnterTime = Date.now();
        return;
      }
    }

    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    if (!item || item === this.#state.activeItem) return;

    this.dispatchEvent(new MegaMenuHoverEvent());

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    if (this.#transitionAbortController) {
      this.#transitionAbortController.abort();
    }

    this.#dropdownOpenInnerWidth = window.innerWidth;

    this.#lockBodyScroll();

    const submenu = findSubmenu(item);
    this.#cachedSubmenu = submenu;

    if (submenu) {
      if (this.#submenuAbortController) {
        this.#submenuAbortController.abort();
      }
      this.#submenuAbortController = new AbortController();
      const { signal } = this.#submenuAbortController;
      submenu.addEventListener('pointerleave', this.#handleSubmenuPointerLeave, { signal });
      const headerEl = document.querySelector('header-component');
      if (headerEl) {
        headerEl.addEventListener('pointerleave', this.#handleHeaderPointerLeave, { signal });
      }
      submenu.addEventListener(
        'click',
        (e) => {
          if (e.target instanceof Element && e.target.closest('.resource-card__wishlist-button')) {
            this.#debouncedDeactivate.cancel();
          }
        },
        { capture: true, signal }
      );

      const submenuInner = submenu.querySelector('.menu-list__submenu-inner');

      const updateHeight = () => {
        const maxHeight = this.#maxSubmenuClipHeightPx(submenu);
        const resultsInner = submenu.querySelector('.predictive-search-results__inner');
        const resultsScrollTop =
          resultsInner instanceof HTMLElement ? resultsInner.scrollTop : 0;

        this.style.setProperty('--submenu-height', `${maxHeight}px`);
        if (resultsInner instanceof HTMLElement) {
          resultsInner.style.maxHeight = 'none';
        }
        void submenu.offsetHeight;

        let submenuHeight = 0;
        if (submenuInner instanceof HTMLElement) {
          submenuHeight = Math.max(submenuInner.scrollHeight, submenuInner.offsetHeight);
          const dropdownContentWrapper = submenuInner.querySelector('.predictive-search-form__content-wrapper');
          if (dropdownContentWrapper instanceof HTMLElement) {
            const innerTop = submenuInner.getBoundingClientRect().top;
            submenuHeight = Math.max(
              submenuHeight,
              dropdownContentWrapper.getBoundingClientRect().bottom - innerTop
            );
          }
          const outerTop = submenu.getBoundingClientRect().top;
          const innerBottom = submenuInner.getBoundingClientRect().bottom;
          submenuHeight = Math.max(submenuHeight, Math.max(0, Math.ceil(innerBottom - outerTop)));
        } else {
          submenuHeight = submenu.scrollHeight || submenu.offsetHeight;
        }

        const finalHeight = Math.min(submenuHeight, maxHeight);
        this.style.setProperty('--submenu-height', `${finalHeight}px`);
        this.style.setProperty('--submenu-opacity', '1');

        // Only `.predictive-search-results__inner` scrolls; cap its max-height so the form footer,
        // `#search-submenu` padding-bottom, and clip (`finalHeight`) stay visible. Do not use
        // `footer.top - riTop` as the cap — when the footer is pushed below the viewport that value
        // grows and fails to shrink the scroller.
        if (resultsInner instanceof HTMLElement) {
          const minH = this.#resultsScrollerMinHeightPx();
          const footer = submenu.querySelector('.predictive-search-form__footer');
          const reserveFooter = this.#footerReserveSpaceForSubmenu(submenu);
          const submenuRect = submenu.getBoundingClientRect();
          const visibleBottom = Math.min(window.innerHeight, submenuRect.top + finalHeight);

          // `#search-submenu` (`.menu-list__submenu-inner`): padding-bottom is below the footer in flow.
          const innerPadBottomPx =
            submenuInner instanceof HTMLElement
              ? Math.ceil(Number.parseFloat(getComputedStyle(submenuInner).paddingBottom) || 0)
              : 0;

          let reservedBelowInner = innerPadBottomPx;
          if (reserveFooter && footer instanceof HTMLElement) {
            const riRect = resultsInner.getBoundingClientRect();
            const fr = footer.getBoundingClientRect();
            const footerLaidOut = fr.height > 0 && (footer.offsetParent !== null || fr.width > 0);
            const gap = Math.max(0, Math.ceil(fr.top - riRect.bottom));
            const footerBlock = footerLaidOut ? Math.ceil(fr.height) : Math.ceil(minH * 4);
            reservedBelowInner += gap + footerBlock;
          }

          const riTop = resultsInner.getBoundingClientRect().top;
          let viewportRoom = Math.floor(visibleBottom - riTop - reservedBelowInner);
          viewportRoom = Math.max(minH, viewportRoom);

          this.style.setProperty('--search-dropdown-results-max-height', `${viewportRoom}px`);
          resultsInner.style.removeProperty('max-height');

          const maxScroll = Math.max(0, resultsInner.scrollHeight - resultsInner.clientHeight);
          resultsInner.scrollTop = Math.min(resultsScrollTop, maxScroll);
        }
      };

      this.#submenuLayoutCallback = updateHeight;

      submenu.removeAttribute('hidden');
      requestAnimationFrame(() => {
        updateHeight();
        requestAnimationFrame(updateHeight);

        const searchInput = submenu.querySelector('input[type="search"]');
        if (searchInput instanceof HTMLInputElement) {
          searchInput.focus();
        }

        this.dispatchEvent(new CustomEvent('theme:search-submenu-open', { bubbles: true }));
      });

      if (submenuInner instanceof HTMLElement) {
        this.#submenuObserver?.disconnect();
        this.#submenuObserver = new MutationObserver(() => {
          requestAnimationFrame(() => {
            updateHeight();
            requestAnimationFrame(updateHeight);
          });
        });

        // `data-search-results` can flip without a childList mutation; keep attribute scope narrow to
        // limit churn (avoids scroll-position resets from unrelated attribute changes).
        this.#submenuObserver.observe(submenuInner, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['data-search-results', 'hidden'],
        });

        this.#submenuResizeObserver?.disconnect();
        const debouncedResizeLayout = debounce(() => {
          requestAnimationFrame(() => {
            updateHeight();
            requestAnimationFrame(updateHeight);
          });
        }, 50);
        this.#submenuResizeObserver = new ResizeObserver(() => {
          debouncedResizeLayout();
        });
        this.#submenuResizeObserver.observe(submenuInner);
      }
    } else {
      this.#cachedSubmenu = null;
      this.#submenuLayoutCallback = null;
      this.style.setProperty('--submenu-height', '0px');
      this.style.setProperty('--submenu-opacity', '1');
      this.style.removeProperty('--search-dropdown-results-max-height');
    }
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    if (event instanceof PointerEvent && event.pointerType !== 'mouse') {
      return;
    }

    if (event instanceof FocusEvent && event.type === 'blur') {
      const relatedTarget = event.relatedTarget instanceof Element ? event.relatedTarget : null;
      const activeElement = document.activeElement instanceof Element ? document.activeElement : null;

      const isFocusWithinSearchMenu =
        this.#isWithinSearchMenu(relatedTarget) || this.#isWithinSearchMenu(activeElement);
      const isFocusInSwymModal = [
        relatedTarget?.closest('.swym-modal, .swym-add-wishlist-modal'),
        activeElement?.closest('.swym-modal, .swym-add-wishlist-modal'),
      ].some(Boolean);

      if (isFocusWithinSearchMenu || isFocusInSwymModal) return;

      if (relatedTarget === null) {
        requestAnimationFrame(() => {
          const el = document.activeElement instanceof Element ? document.activeElement : null;
          if (el?.closest('.swym-modal, .swym-add-wishlist-modal')) return;
          const item = findMenuItem(event.target instanceof Element ? event.target : null);
          if (item === this.#state.activeItem) this.#debouncedDeactivate();
        });
        return;
      }
    }

    const item = findMenuItem(event.target);

    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * @param {HTMLElement | null} [item]
   * @param {boolean} [force]
   */
  #deactivate = (item = this.#state.activeItem, force = false) => {
    if (!item || item !== this.#state.activeItem) return;
    if (!force && this.submenuHovered) return;
    if (!force && isSwymWishlistModalOpen()) return;

    this.#dropdownOpenInnerWidth = null;

    if (this.#submenuAbortController) {
      this.#submenuAbortController.abort();
      this.#submenuAbortController = null;
    }

    this.#submenuObserver?.disconnect();
    this.#submenuObserver = null;
    this.#submenuResizeObserver?.disconnect();
    this.#submenuResizeObserver = null;

    if (this.#transitionAbortController) {
      this.#transitionAbortController.abort();
    }

    const submenu = findSubmenu(item);

    this.#cachedSubmenu = null;
    this.#submenuLayoutCallback = null;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.style.removeProperty('--search-dropdown-results-max-height');

    this.#lastPointerEnterTime = null;

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    if (submenu) {
      this.#transitionAbortController = new AbortController();
      const { signal } = this.#transitionAbortController;
      let transitionCompleted = false;

      /** @type {ReturnType<typeof setTimeout> | undefined} */
      let fallbackTimeoutId;

      const finalizeClose = () => {
        if (transitionCompleted) return;
        transitionCompleted = true;
        if (fallbackTimeoutId !== undefined) {
          clearTimeout(fallbackTimeoutId);
        }

        submenu.removeEventListener('transitionend', handleTransitionEnd);

        if (this.#state.activeItem) {
          item.removeAttribute('data-animating');
          this.#transitionAbortController = null;
          return;
        }

        item.removeAttribute('data-animating');
        submenu.setAttribute('hidden', '');
        this.#unlockBodyScroll();
        this.#transitionAbortController = null;
      };

      /**
       * @param {TransitionEvent} event
       */
      const handleTransitionEnd = (event) => {
        if (event.propertyName === 'clip-path') {
          finalizeClose();
        }
      };

      submenu.addEventListener('transitionend', handleTransitionEnd);

      fallbackTimeoutId = setTimeout(() => {
        if (!transitionCompleted && !signal.aborted) {
          finalizeClose();
        }
      }, getSearchMenuCloseFallbackDelayMs());

      signal.addEventListener('abort', () => {
        if (fallbackTimeoutId !== undefined) {
          clearTimeout(fallbackTimeoutId);
        }
        finalizeClose();
      });
    } else {
      item.removeAttribute('data-animating');
      this.#unlockBodyScroll();
    }
  };

  #lockBodyScroll() {
    if (this.#scrollY !== null) return;

    this.#scrollY = getDocumentScrollY();

    document.body.style.overflowY = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.#scrollY}px`;
    document.body.style.width = '100%';
  }

  #unlockBodyScroll() {
    if (this.#scrollY === null) return;

    const scrollY = this.#scrollY;
    this.#scrollY = null;

    document.body.style.overflowY = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';

    window.scrollTo({ top: scrollY, behavior: 'instant' });
  }

  /**
   * If the dropdown still considers scroll locked (#scrollY) but body styles were cleared
   * (e.g. search modal `closeDialog`), re-apply the same lock without changing #scrollY.
   */
  reapplyBodyScrollLockIfActive() {
    if (this.#scrollY === null) return;
    if (!this.#state.activeItem) return;
    if (document.body.style.position === 'fixed') return;

    const y = this.#scrollY;
    document.body.style.overflowY = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${y}px`;
    document.body.style.width = '100%';
  }

  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);
}

if (!customElements.get('search-menu')) {
  customElements.define('search-menu', SearchMenu);
}

/**
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const menuitem = element.closest('[ref="menuitem"]') || element.querySelector('[ref="menuitem"]');
  return menuitem instanceof HTMLElement ? menuitem : null;
}

/**
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
